import React, { useCallback, useEffect, useState } from "react";
import type {
  SearchConsoleConnectionView,
  SearchConsoleProblemCode,
  SearchPerformanceDay,
  SearchPerformanceRow,
  SearchPerformanceTotals,
  SearchPerformanceView,
  SiteId,
  TrafficInsufficientReason,
  TrafficMeasurementView,
} from "@siteagain/shared-types";

import { intlLocale, useLocale, type Locale } from "./i18n";
import { formatDay, operatorFetch, operatorPost, relativeTime } from "./operator/format";
import "./search-console.css";

/**
 * How the site is doing on Google, in the customer's Growth view (ADR-0027).
 *
 * Every number here is Google's own, pulled from the customer's Search Console, and says through which
 * day it runs. Nothing is estimated, and a change in traffic is never said to be caused by SiteAgain:
 * results after a change are shown next to what the rest of the site did over the same days.
 */
const EN = {
  eyebrow: "Search Console",
  title: "How people find you on Google",
  intro: "Real numbers from Google: how often your site appeared in search, and how many people clicked.",
  unavailable: "The Google connection is not available yet. It will appear here as soon as it is switched on.",
  connectTitle: "Connect Google Search Console",
  connectBody: "SiteAgain reads your search numbers (clicks, appearances, average position) so each month's report can show what happened after a change. Access is read-only: it cannot change anything in your Google account or your site.",
  connectList: ["Read-only access to Search Console", "You can disconnect at any time", "Disconnecting deletes what we pulled"],
  connect: "Connect Google",
  connecting: "Opening Google…",
  reconnect: "Reconnect Google",
  chooseTitle: "Which property is this site?",
  chooseBody: "Your Google account has more than one Search Console property for this site. Choose the one to read.",
  choose: "Use this property",
  syncing: "Connected. The first numbers arrive within a few minutes.",
  syncingMore: "Google publishes its numbers about three days late, so the newest days will not show yet.",
  connectedAs: (email: string | null, property: string | null) => [email ? `Connected as ${email}` : "Connected", property].filter(Boolean).join(" · "),
  updated: (ago: string) => `Updated ${ago}`,
  disconnect: "Disconnect",
  disconnectConfirm: "Disconnect Search Console? Access is revoked at Google, and the numbers pulled so far are deleted from SiteAgain. Monthly reports already written stay.",
  disconnectNow: "Disconnect and delete",
  cancel: "Cancel",
  brokenTitle: "SiteAgain can no longer read your Search Console",
  brokenBody: "The numbers below stop at the last day we received them. Reconnect to bring them up to date.",
  problem: {
    token_revoked: "Access was withdrawn in your Google account.",
    access_lost: "Your Google account no longer has access to this property.",
    property_missing: "This property no longer exists in Search Console.",
    provider_unavailable: "Google did not answer. We will try again.",
    rate_limited: "Google asked us to slow down. We will try again.",
    missing_scope: "The permission to read Search Console was not granted.",
  } satisfies Record<SearchConsoleProblemCode, string>,
  window: (from: string, to: string) => `${from} – ${to}`,
  lag: (through: string) => `Numbers run through ${through}. Google publishes them about three days late.`,
  tiles: { clicks: "Clicks", impressions: "Appearances", ctr: "Click rate", position: "Average position" },
  tileHint: { clicks: "People who clicked through to your site", impressions: "Times your site appeared in results", ctr: "Clicks per appearance", position: "1 is the top of the results" },
  versus: (days: number) => `vs the previous ${days} days`,
  noComparison: "No earlier period to compare yet",
  placesHigher: (places: string) => `${places} places higher`,
  placesLower: (places: string) => `${places} places lower`,
  chartLabel: (from: string, to: string, clicks: string) => `Daily clicks from ${from} to ${to}, ${clicks} in total`,
  chartTitle: "Clicks per day",
  peak: (count: string) => `peak ${count}`,
  pages: "Pages people land on",
  queries: "What people searched for",
  clicksUnit: (count: string) => `${count} clicks`,
  resultsTitle: "What happened after our changes",
  resultsIntro: "Clicks on the pages we changed, before and after, next to the rest of your site over the same days. This shows what moved, not why.",
  window7: "7 days",
  window14: "14 days",
  window28: "28 days",
  after: (window: string) => `${window} after`,
  scopePages: (pages: string) => `Pages: ${pages}`,
  scopeSite: "Whole site",
  verdict: { improved: "More visits than the rest of the site", neutral: "No clear difference", declined: "Fewer visits than the rest of the site", insufficient: "Not enough to say" },
  clicksMoved: (before: string, after: string, percent: string) => `Clicks ${before} → ${after} (${percent})`,
  impressionsMoved: (before: string, after: string, percent: string) => `Appearances ${before} → ${after} (${percent})`,
  restMoved: (percent: string) => `rest of the site ${percent}`,
  confidence: { high: "High confidence", medium: "Medium confidence", low: "Low confidence", insufficient: "" },
  overlap: (count: number) => `${count} other ${count === 1 ? "change" : "changes"} on the same pages in this period`,
  reason: {
    baseline_incomplete: "There was not enough history before the change to compare.",
    window_incomplete: "Google had not published the whole period yet.",
    too_little_traffic: "Too few visits to compare fairly.",
    pages_without_data: "These pages did not appear in Google search during the period.",
  } satisfies Record<TrafficInsufficientReason, string>,
  noResults: "Results appear here 7, 14 and 28 days after a change goes live.",
  caveat: "These are observations from Google's numbers. Seasons, demand, competitors and Google's own updates also move traffic.",
  notices: {
    connected: "Google Search Console is connected. Your numbers are on their way.",
    choose_property: "Google is connected. Choose which property to read.",
  },
  failures: {
    cancelled: "The connection was cancelled. Nothing was changed.",
    invalid_state: "That connection link expired. Please try again.",
    wrong_user: "Please sign in with the account that owns this site, then try again.",
    no_refresh_token: "Google did not grant lasting access. Please try again and accept the permission.",
    missing_scope: "The permission to read Search Console was not granted. Please try again and tick it.",
    no_matching_property: "That Google account has no Search Console property for this site. Try another account, or add the site in Search Console first.",
    provider_unavailable: "Google could not be reached. Please try again in a moment.",
    not_configured: "The Google connection is not available yet.",
  } as Record<string, string>,
  loadFailed: "Could not load your search numbers.",
  actionFailed: "That did not work. Please try again.",
  close: "Dismiss",
};

const FR: typeof EN = {
  eyebrow: "Search Console",
  title: "Comment on vous trouve sur Google",
  intro: "Les chiffres réels de Google : combien de fois votre site est apparu dans les résultats, et combien de personnes ont cliqué.",
  unavailable: "La connexion à Google n'est pas encore disponible. Elle apparaîtra ici dès qu'elle sera activée.",
  connectTitle: "Connecter Google Search Console",
  connectBody: "SiteAgain lit vos chiffres de recherche (clics, affichages, position moyenne) pour que le rapport de chaque mois montre ce qui s'est passé après une modification. L'accès est en lecture seule : rien ne peut être modifié dans votre compte Google ni sur votre site.",
  connectList: ["Accès en lecture seule à Search Console", "Vous pouvez vous déconnecter à tout moment", "Se déconnecter supprime ce que nous avons récupéré"],
  connect: "Connecter Google",
  connecting: "Ouverture de Google…",
  reconnect: "Reconnecter Google",
  chooseTitle: "Quelle propriété est ce site ?",
  chooseBody: "Votre compte Google a plusieurs propriétés Search Console pour ce site. Choisissez celle à lire.",
  choose: "Utiliser cette propriété",
  syncing: "Connecté. Les premiers chiffres arrivent dans quelques minutes.",
  syncingMore: "Google publie ses chiffres avec environ trois jours de retard : les derniers jours n'apparaîtront pas encore.",
  connectedAs: (email, property) => [email ? `Connecté en tant que ${email}` : "Connecté", property].filter(Boolean).join(" · "),
  updated: (ago) => `Mis à jour ${ago}`,
  disconnect: "Déconnecter",
  disconnectConfirm: "Déconnecter Search Console ? L'accès est révoqué chez Google et les chiffres récupérés sont supprimés de SiteAgain. Les rapports mensuels déjà écrits restent.",
  disconnectNow: "Déconnecter et supprimer",
  cancel: "Annuler",
  brokenTitle: "SiteAgain ne peut plus lire votre Search Console",
  brokenBody: "Les chiffres ci-dessous s'arrêtent au dernier jour reçu. Reconnectez-vous pour les mettre à jour.",
  problem: {
    token_revoked: "L'accès a été retiré dans votre compte Google.",
    access_lost: "Votre compte Google n'a plus accès à cette propriété.",
    property_missing: "Cette propriété n'existe plus dans Search Console.",
    provider_unavailable: "Google n'a pas répondu. Nous réessaierons.",
    rate_limited: "Google nous a demandé de ralentir. Nous réessaierons.",
    missing_scope: "L'autorisation de lire Search Console n'a pas été accordée.",
  },
  window: (from, to) => `${from} – ${to}`,
  lag: (through) => `Les chiffres vont jusqu'au ${through}. Google les publie avec environ trois jours de retard.`,
  tiles: { clicks: "Clics", impressions: "Affichages", ctr: "Taux de clic", position: "Position moyenne" },
  tileHint: { clicks: "Personnes qui ont cliqué vers votre site", impressions: "Fois où votre site est apparu dans les résultats", ctr: "Clics par affichage", position: "1 est le haut des résultats" },
  versus: (days) => `par rapport aux ${days} jours précédents`,
  noComparison: "Pas encore de période antérieure à comparer",
  placesHigher: (places) => `${places} places plus haut`,
  placesLower: (places) => `${places} places plus bas`,
  chartLabel: (from, to, clicks) => `Clics par jour du ${from} au ${to}, ${clicks} au total`,
  chartTitle: "Clics par jour",
  peak: (count) => `pic ${count}`,
  pages: "Pages d'arrivée",
  queries: "Ce que les gens ont cherché",
  clicksUnit: (count) => `${count} clics`,
  resultsTitle: "Ce qui s'est passé après nos modifications",
  resultsIntro: "Les clics sur les pages modifiées, avant et après, à côté du reste de votre site sur les mêmes jours. Cela montre ce qui a bougé, pas pourquoi.",
  window7: "7 jours",
  window14: "14 jours",
  window28: "28 jours",
  after: (window) => `${window} après`,
  scopePages: (pages) => `Pages : ${pages}`,
  scopeSite: "Tout le site",
  verdict: { improved: "Plus de visites que le reste du site", neutral: "Pas de différence nette", declined: "Moins de visites que le reste du site", insufficient: "Pas assez pour conclure" },
  clicksMoved: (before, after, percent) => `Clics ${before} → ${after} (${percent})`,
  impressionsMoved: (before, after, percent) => `Affichages ${before} → ${after} (${percent})`,
  restMoved: (percent) => `reste du site ${percent}`,
  confidence: { high: "Confiance élevée", medium: "Confiance moyenne", low: "Confiance faible", insufficient: "" },
  overlap: (count) => `${count} autre${count === 1 ? "" : "s"} modification${count === 1 ? "" : "s"} sur les mêmes pages pendant cette période`,
  reason: {
    baseline_incomplete: "L'historique avant la modification était insuffisant pour comparer.",
    window_incomplete: "Google n'avait pas encore publié toute la période.",
    too_little_traffic: "Trop peu de visites pour comparer équitablement.",
    pages_without_data: "Ces pages ne sont pas apparues dans la recherche Google pendant la période.",
  },
  noResults: "Les résultats apparaissent ici 7, 14 et 28 jours après la mise en ligne d'une modification.",
  caveat: "Ce sont des observations tirées des chiffres de Google. Les saisons, la demande, la concurrence et les mises à jour de Google font aussi bouger le trafic.",
  notices: {
    connected: "Google Search Console est connecté. Vos chiffres arrivent.",
    choose_property: "Google est connecté. Choisissez la propriété à lire.",
  },
  failures: {
    cancelled: "La connexion a été annulée. Rien n'a été modifié.",
    invalid_state: "Ce lien de connexion a expiré. Veuillez réessayer.",
    wrong_user: "Connectez-vous avec le compte propriétaire de ce site, puis réessayez.",
    no_refresh_token: "Google n'a pas accordé d'accès durable. Réessayez en acceptant l'autorisation.",
    missing_scope: "L'autorisation de lire Search Console n'a pas été accordée. Réessayez en la cochant.",
    no_matching_property: "Ce compte Google n'a pas de propriété Search Console pour ce site. Essayez un autre compte, ou ajoutez d'abord le site dans Search Console.",
    provider_unavailable: "Google est injoignable. Réessayez dans un instant.",
    not_configured: "La connexion à Google n'est pas encore disponible.",
  },
  loadFailed: "Impossible de charger vos chiffres de recherche.",
  actionFailed: "Cela n'a pas fonctionné. Veuillez réessayer.",
  close: "Fermer",
};

const copyFor = (locale: Locale) => (locale === "fr" ? FR : EN);

const STASH_KEY = "siteagain_search_console_result";

interface SearchConsoleResult {
  result: "connected" | "choose_property" | "error";
  reason: string | null;
  siteId: string | null;
}

/**
 * Reads `?searchConsole=…&site=…&reason=…` once, where Google's redirect lands, removes it from the
 * address bar and keeps it for the tab, so the app can open the right site and say what happened.
 */
export function adoptSearchConsoleResult(): SearchConsoleResult | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("searchConsole");
    if (params.has("searchConsole")) {
      const reason = params.get("reason");
      const siteId = params.get("site");
      for (const key of ["searchConsole", "reason", "site"]) params.delete(key);
      const query = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
      if (result === "connected" || result === "choose_property" || result === "error") {
        const stored: SearchConsoleResult = { result, reason, siteId: siteId && /^site_[A-Za-z0-9_-]+$/u.test(siteId) ? siteId : null };
        try { sessionStorage.setItem(STASH_KEY, JSON.stringify(stored)); } catch {}
        return stored;
      }
    }
  } catch {}
  return peekSearchConsoleResult();
}

export function peekSearchConsoleResult(): SearchConsoleResult | null {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    return raw ? JSON.parse(raw) as SearchConsoleResult : null;
  } catch {
    return null;
  }
}

function consumeSearchConsoleResult(siteId: string): SearchConsoleResult | null {
  const stored = peekSearchConsoleResult();
  if (!stored || (stored.siteId !== null && stored.siteId !== siteId)) return null;
  try { sessionStorage.removeItem(STASH_KEY); } catch {}
  return stored;
}

// ---------------------------------------------------------------------------

export function SearchConsoleSection({ siteId }: { siteId: SiteId }) {
  const locale = useLocale();
  const t = copyFor(locale);
  const [configured, setConfigured] = useState(true);
  const [connection, setConnection] = useState<SearchConsoleConnectionView | null>(null);
  const [performance, setPerformance] = useState<SearchPerformanceView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState("");
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [chosen, setChosen] = useState("");

  const load = useCallback(async () => {
    try {
      const [state, view] = await Promise.all([
        operatorFetch<{ configured: boolean; connection: SearchConsoleConnectionView | null }>(`/sites/${siteId}/search-console`),
        operatorFetch<{ performance: SearchPerformanceView }>(`/sites/${siteId}/search-performance?days=28`),
      ]);
      setConfigured(state.configured);
      setConnection(state.connection);
      setPerformance(view.performance);
      setError("");
    } catch {
      setError(t.loadFailed);
    } finally {
      setLoaded(true);
    }
  }, [siteId, t.loadFailed]);

  useEffect(() => {
    setLoaded(false);
    void load();
  }, [load]);

  // What Google's redirect said, shown once.
  useEffect(() => {
    const result = consumeSearchConsoleResult(siteId);
    if (!result) return;
    if (result.result === "error") setNotice({ tone: "bad", text: t.failures[result.reason ?? ""] ?? t.actionFailed });
    else setNotice({ tone: "good", text: t.notices[result.result] });
  }, [siteId, t]);

  // Connected but nothing pulled yet: look again until the first numbers land.
  const waitingForFirstNumbers = connection?.status === "active" && performance !== null && !performance.hasData;
  useEffect(() => {
    if (!waitingForFirstNumbers) return;
    const timer = window.setInterval(() => { void load(); }, 15_000);
    return () => window.clearInterval(timer);
  }, [waitingForFirstNumbers, load]);

  async function run(key: string, task: () => Promise<void>) {
    setBusy(key);
    setNotice(null);
    try {
      await task();
    } catch (reason) {
      setNotice({ tone: "bad", text: reason instanceof Error && reason.message ? reason.message : t.actionFailed });
    } finally {
      setBusy("");
    }
  }

  const connect = () => run("connect", async () => {
    const response = await operatorPost<{ authorizationUrl: string }>(`/sites/${siteId}/search-console/connect`);
    window.location.assign(response.authorizationUrl);
  });
  const selectProperty = () => run("property", async () => {
    await operatorPost(`/sites/${siteId}/search-console/property`, { property: chosen });
    setChosen("");
    await load();
  });
  const disconnect = () => run("disconnect", async () => {
    await operatorFetch(`/sites/${siteId}/search-console`, { method: "DELETE" });
    setConfirmingDisconnect(false);
    await load();
  });

  return (
    <section className="growth-section sc" aria-labelledby="sc-title">
      <div className="growth-section-heading">
        <div><small>{t.eyebrow}</small><h2 id="sc-title">{t.title}</h2></div>
      </div>
      <p className="sc-intro">{t.intro}</p>

      {notice && (
        <p className={`sc-notice ${notice.tone}`} role={notice.tone === "bad" ? "alert" : "status"}>
          <span>{notice.text}</span>
          <button type="button" aria-label={t.close} onClick={() => setNotice(null)}>×</button>
        </p>
      )}
      {error && <p className="sc-notice bad" role="alert">{error}</p>}

      {!loaded && !error && <div className="sc-skeleton" aria-hidden="true"><i /><i /><i /></div>}

      {loaded && !error && !connection && (
        configured ? (
          <div className="sc-connect">
            <h3>{t.connectTitle}</h3>
            <p>{t.connectBody}</p>
            <ul>{t.connectList.map((line) => <li key={line}>{line}</li>)}</ul>
            <button type="button" className="sc-primary" onClick={() => void connect()} disabled={busy === "connect"}>
              {busy === "connect" ? t.connecting : t.connect}
            </button>
          </div>
        ) : (
          <p className="sc-empty">{t.unavailable}</p>
        )
      )}

      {loaded && connection?.status === "pending_property" && (
        <div className="sc-connect">
          <h3>{t.chooseTitle}</h3>
          <p>{t.chooseBody}</p>
          <fieldset className="sc-properties">
            <legend className="sc-visually-hidden">{t.chooseTitle}</legend>
            {connection.availableProperties.map((option) => (
              <label key={option.siteUrl} className={chosen === option.siteUrl ? "is-chosen" : ""}>
                <input type="radio" name="sc-property" value={option.siteUrl} checked={chosen === option.siteUrl} onChange={() => setChosen(option.siteUrl)} />
                <span>{option.siteUrl.replace(/^sc-domain:/u, "")}<small>{option.siteUrl.startsWith("sc-domain:") ? "Domain" : "URL prefix"}</small></span>
              </label>
            ))}
          </fieldset>
          <button type="button" className="sc-primary" disabled={!chosen || busy === "property"} onClick={() => void selectProperty()}>{t.choose}</button>
        </div>
      )}

      {loaded && connection?.status === "needs_reconnect" && (
        <div className="sc-broken" role="alert">
          <h3>{t.brokenTitle}</h3>
          <p>{connection.lastError ? t.problem[connection.lastError.code] : ""} {t.brokenBody}</p>
          <button type="button" className="sc-primary" onClick={() => void connect()} disabled={busy === "connect"}>{busy === "connect" ? t.connecting : t.reconnect}</button>
        </div>
      )}

      {loaded && connection && connection.status !== "pending_property" && (
        <>
          {performance?.hasData && performance.totals && performance.window
            ? <Performance view={performance} totals={performance.totals} window={performance.window} copy={t} locale={locale} />
            : connection.status === "active" && (
              <div className="sc-syncing" role="status">
                <span className="sc-pulse" aria-hidden="true" />
                <div><strong>{t.syncing}</strong><small>{t.syncingMore}</small></div>
              </div>
            )}

          <footer className="sc-connection">
            <span>
              {t.connectedAs(connection.googleEmail, connection.property?.replace(/^sc-domain:/u, "") ?? null)}
              {connection.lastSyncedAt && <> · {t.updated(relativeTime(connection.lastSyncedAt, locale))}</>}
            </span>
            {confirmingDisconnect ? (
              <div className="sc-confirm" role="alert">
                <p>{t.disconnectConfirm}</p>
                <div>
                  <button type="button" className="sc-danger" onClick={() => void disconnect()} disabled={busy === "disconnect"}>{t.disconnectNow}</button>
                  <button type="button" onClick={() => setConfirmingDisconnect(false)}>{t.cancel}</button>
                </div>
              </div>
            ) : (
              <button type="button" className="sc-quiet" onClick={() => setConfirmingDisconnect(true)}>{t.disconnect}</button>
            )}
          </footer>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

type Copy = typeof EN;

function Performance({
  view,
  totals,
  window,
  copy,
  locale,
}: {
  view: SearchPerformanceView;
  totals: SearchPerformanceTotals;
  window: { from: string; to: string; days: number };
  copy: Copy;
  locale: Locale;
}) {
  const number = new Intl.NumberFormat(intlLocale(locale));
  const previous = view.previousTotals;
  const clicksChange = percentChange(totals.clicks, previous?.clicks ?? null);
  const impressionsChange = percentChange(totals.impressions, previous?.impressions ?? null);
  const ctr = totals.ctr === null ? "—" : `${(totals.ctr * 100).toFixed(1)}%`;
  const ctrChange = totals.ctr !== null && previous?.ctr != null ? (totals.ctr - previous.ctr) * 100 : null;
  const positionChange = totals.position !== null && previous?.position != null ? totals.position - previous.position : null;

  return (
    <>
      <p className="sc-window">
        {copy.window(formatDay(window.from, locale), formatDay(window.to, locale))} · {copy.lag(formatDay(view.dataThrough ?? window.to, locale))}
      </p>

      <div className="sc-tiles">
        <Tile label={copy.tiles.clicks} hint={copy.tileHint.clicks} value={number.format(totals.clicks)} change={percentChip(clicksChange, locale)} direction={direction(clicksChange)} versus={previous ? copy.versus(window.days) : copy.noComparison} />
        <Tile label={copy.tiles.impressions} hint={copy.tileHint.impressions} value={number.format(totals.impressions)} change={percentChip(impressionsChange, locale)} direction={direction(impressionsChange)} versus={previous ? copy.versus(window.days) : copy.noComparison} />
        <Tile label={copy.tiles.ctr} hint={copy.tileHint.ctr} value={ctr} change={ctrChange === null ? null : `${ctrChange > 0 ? "+" : ""}${ctrChange.toFixed(1)} pt`} direction={direction(ctrChange)} versus={previous ? copy.versus(window.days) : copy.noComparison} />
        <Tile
          label={copy.tiles.position}
          hint={copy.tileHint.position}
          value={totals.position === null ? "—" : totals.position.toFixed(1)}
          change={positionChange === null || Math.abs(positionChange) < 0.05 ? null : positionChange < 0 ? copy.placesHigher(Math.abs(positionChange).toFixed(1)) : copy.placesLower(positionChange.toFixed(1))}
          // A smaller position number is better.
          direction={positionChange === null ? "flat" : positionChange < -0.05 ? "up" : positionChange > 0.05 ? "down" : "flat"}
          versus={previous ? copy.versus(window.days) : copy.noComparison}
        />
      </div>

      <Sparkline days={view.series} copy={copy} locale={locale} from={window.from} to={window.to} total={totals.clicks} />

      <div className="sc-lists">
        <RankedList title={copy.pages} rows={view.topPages} copy={copy} locale={locale} asPath />
        <RankedList title={copy.queries} rows={view.topQueries} copy={copy} locale={locale} />
      </div>

      <div className="sc-results">
        <h3>{copy.resultsTitle}</h3>
        <p className="sc-intro">{copy.resultsIntro}</p>
        {view.measurements.length === 0 ? (
          <p className="sc-empty">{copy.noResults}</p>
        ) : (
          <ul>{view.measurements.map((item) => <Measurement key={item.id} item={item} copy={copy} locale={locale} />)}</ul>
        )}
        <p className="sc-caveat">{copy.caveat}</p>
      </div>
    </>
  );
}

function Tile({ label, hint, value, change, direction: moved, versus }: { label: string; hint: string; value: string; change: string | null; direction: "up" | "down" | "flat"; versus: string }) {
  return (
    <article className="sc-tile" title={hint}>
      <small>{label}</small>
      <strong>{value}</strong>
      <p className={`sc-change ${moved}`}>
        {change ? <><b aria-hidden="true">{moved === "up" ? "▲" : moved === "down" ? "▼" : "•"}</b> {change}</> : <span>&nbsp;</span>}
      </p>
      <em>{versus}</em>
    </article>
  );
}

function Sparkline({ days, copy, locale, from, to, total }: { days: SearchPerformanceDay[]; copy: Copy; locale: Locale; from: string; to: string; total: number }) {
  if (days.length < 2) return null;
  const width = 640;
  const height = 120;
  const max = Math.max(1, ...days.map((day) => day.clicks));
  const step = width / (days.length - 1);
  const points = days.map((day, index) => [index * step, height - 8 - (day.clicks / max) * (height - 24)] as const);
  const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `0,${height} ${line} ${width},${height}`;
  const last = points[points.length - 1];
  const label = copy.chartLabel(formatDay(from, locale), formatDay(to, locale), new Intl.NumberFormat(intlLocale(locale)).format(total));
  return (
    <figure className="sc-chart">
      <figcaption><span>{copy.chartTitle}</span><span>{copy.peak(new Intl.NumberFormat(intlLocale(locale)).format(max))}</span></figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label={label}>
        <title>{label}</title>
        <polygon points={area} className="sc-area" />
        <polyline points={line} className="sc-line" vectorEffect="non-scaling-stroke" />
        {last && <circle cx={last[0]} cy={last[1]} r="4" className="sc-dot" />}
      </svg>
      <div className="sc-axis"><span>{formatDay(from, locale)}</span><span>{formatDay(to, locale)}</span></div>
    </figure>
  );
}

function RankedList({ title, rows, copy, locale, asPath = false }: { title: string; rows: SearchPerformanceRow[]; copy: Copy; locale: Locale; asPath?: boolean }) {
  const number = new Intl.NumberFormat(intlLocale(locale));
  const label = (key: string) => {
    if (!asPath) return key;
    try { return new URL(key).pathname + new URL(key).search || "/"; } catch { return key; }
  };
  return (
    <section className="sc-ranked">
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="sc-empty">—</p> : (
        <ol>
          {rows.slice(0, 5).map((row) => (
            <li key={row.key}>
              <span title={row.key}>{label(row.key)}</span>
              <strong>{copy.clicksUnit(number.format(row.clicks))}</strong>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Measurement({ item, copy, locale }: { item: TrafficMeasurementView; copy: Copy; locale: Locale }) {
  const number = new Intl.NumberFormat(intlLocale(locale));
  const windowLabel = item.windowDays === 7 ? copy.window7 : item.windowDays === 14 ? copy.window14 : copy.window28;
  const primaryIsClicks = item.change.primaryMetric === "clicks";
  const percent = percentChip(primaryIsClicks ? item.change.clicksPercent : item.change.impressionsPercent, locale) ?? "";
  const rest = item.change.restClicksPercent === null ? null : percentChip(item.change.restClicksPercent, locale);
  const moved = primaryIsClicks
    ? copy.clicksMoved(number.format(item.before.clicks), number.format(item.after.clicks), percent)
    : copy.impressionsMoved(number.format(item.before.impressions), number.format(item.after.impressions), percent);
  return (
    <li className={`sc-result verdict-${item.verdict}`}>
      <div className="sc-result-head">
        <strong>{item.changeSummary ?? "—"}</strong>
        <span>{copy.after(windowLabel)}</span>
      </div>
      <p className="sc-result-scope">{item.scope === "pages" ? copy.scopePages(item.pages.slice(0, 3).join(", ")) : copy.scopeSite}</p>
      <p className="sc-result-verdict">{copy.verdict[item.verdict]}</p>
      {item.verdict === "insufficient"
        ? <p className="sc-result-detail">{item.reason ? copy.reason[item.reason] : ""}</p>
        : (
          <p className="sc-result-detail">
            {moved}{rest ? ` · ${copy.restMoved(rest)}` : ""}
          </p>
        )}
      <p className="sc-result-meta">
        {copy.confidence[item.confidence]}
        {item.overlappingChanges > 0 && <> · {copy.overlap(item.overlappingChanges)}</>}
      </p>
    </li>
  );
}

function percentChange(current: number, previous: number | null): number | null {
  return previous === null || previous <= 0 ? null : ((current - previous) / previous) * 100;
}

function direction(value: number | null): "up" | "down" | "flat" {
  return value === null || Math.abs(value) < 0.5 ? "flat" : value > 0 ? "up" : "down";
}

function percentChip(value: number | null, locale: Locale): string | null {
  if (value === null) return null;
  const formatted = new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(Math.round(value));
  return `${value > 0 ? "+" : ""}${formatted}%`;
}
